/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types, getEnv } from 'mobx-state-tree';

export default types.model({
	scopeId: types.string
}).views((self) => ({

	/**
	* Get the total active filter count from the given scope id (aka layout model id for the scope)
	* @param {String} scopeId
	* @returns {int} filterCount
	*/
	getFilterCount() {
		return self.filterCount;
	},

	getCurrentExtensionScope() {
		if (self._filterDockExtensionView) {
			return self._filterDockExtensionView.getApi().currentScope();
		}
	}

})).volatile(() => ({
	dashboardApi: null,
	exploreVM: null,
	_disposeEvents: [],
	filterCount: null,
	filterCountExtensionFn: null,
	filterDockOpened: false
})).actions(self => ({

	afterCreate() {
		self.dashboardApi = getEnv(self).dashboardApi;
		self._registerDashboardApiEvents();
		self.exploreVM = getEnv(self).exploreVM;
	},

	registerFilterCountPayloadExtension(fn) {
		self.filterCountExtensionFn = fn;
	},

	setFilterDockOpened( opened ) {
		self.filterDockOpened = opened;
	},

	/**
	* Get and filter out all of the active dock filters within the scope in pageContext
	* sets filterCount to the number of active filters in current context
	* eg. PageContext Spec Sample
	* if the scope is 'page1:2', the number of active dock filters will be 1 in the below spec
	* {
		 origin: 'filter',
		 sourceId: '123',
		 scope: 'page1:1'
	 }, {
		 origin: 'filter',
		 sourceId: '123',
		 scope: 'page1:2'
	 }, {
		 origin: 'visualization',
		 sourceId: '123',
		 scope: 'page1:2'
	 }
	 */

	fetchFilterCount() {
		let selector = { scope: self.scopeId, origin: 'filter' };
		if (self.filterCountExtensionFn) {
			selector = self.filterCountExtensionFn(selector);
		}
		self.filterCount = self.dashboardApi.getFeature('GlobalFilters').getFilterList(selector).length;
	},

	beforeDestroy() {
		// Remove the events
		self._disposeEvents && self._disposeEvents.forEach(dispose => dispose && dispose.remove());
		self.dashboardApi = null;
	},

	switchDatasource(activeDataSourceId) {
		if (self._filterDockExtensionView) {
			self._filterDockExtensionView.getApi().switchDatasource(activeDataSourceId);
			self.fetchFilterCount();
		}
	},

	initializeFilterDock() {
		return self.dashboardApi.getDashboardSvc('.CanvasExtensions')
			.then((canvasExtensions) => {
				self.canvasExtensions = canvasExtensions;
				if (self.canvasExtensions) {
					self._filterDockExtensionView = self.canvasExtensions.getExtension('filterDock');
					self.filterDockCollapseView = self.canvasExtensions.getExtension('filterDockCollapseControl');
					self.fetchFilterCount();
				}
			});
	},

	_switchDatasourceSilently() {
		if (self._filterDockExtensionView) {
			if (self.exploreVM && self.exploreVM.isStartingPointsDisplayed()) {
				self._filterDockExtensionView.getApi().switchDatasource(self.dashboardApi.getActiveDataSourceId(), { silent: true });
			} else {
				self._filterDockExtensionView.getApi().switchDatasource(null, { silent: true });
			}
		}
		self.fetchFilterCount();
	},

	/**
	*  register to listen to dashboard api events and fetch filter count
	 *
	 *  listens to the following scenarios
	 *  1) collapse - user close the filter dock in compare card
	 * 	2) filterIndicator:removeEntry - use delete a filter entry from filter indicator
	 *  3) brushing:undoRedo - undo / redo of pagecontext change. Misleading event name, not just for brushing.
	 */
	_registerDashboardApiEvents(){
		self._disposeEvents.push(
			self.dashboardApi.on('filterDock:collapse', () => {
				self.fetchFilterCount();
				self.setFilterDockOpened(false);
			})
		);
		self._disposeEvents.push(
			self.dashboardApi.on('filterIndicator:removeEntry', () => {
				self.fetchFilterCount();
			})
		);
		self._disposeEvents.push(
			self.dashboardApi.on('brushing:undoRedo', () => {
				self.fetchFilterCount();
			})
		);
		self._disposeEvents.push(
			self.dashboardApi.on('tab:tabChanged', () => {
				self._switchDatasourceSilently();
			})
		);
	}
}));



// WEBPACK FOOTER //
// ./src/js/features/filters/FilterDockModel.js